import { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuth from 'src/hooks/useAuth';
import { useSelector } from 'src/store';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { BACKOFFICE_MAINTENANCE_ROUTE } from 'src/utils/backOfficeMaintenance';

type BackOfficeMaintenanceGuardProps = {
  children: ReactNode;
  mode?: 'gate' | 'screen';
};

const BackOfficeMaintenanceGuard: FC<BackOfficeMaintenanceGuardProps> = ({
  children,
  mode = 'gate',
}) => {
  const auth = useAuth();
  const location = useLocation();
  const { isBackOfficeMaintenance, maintenanceLoaded } = useSelector(
    (state) => state.settings
  );

  const isOnMaintenancePage = location.pathname === BACKOFFICE_MAINTENANCE_ROUTE;

  if (!auth.isInitialized || !maintenanceLoaded) {
    return <SuspenseLoader />;
  }

  if (mode === 'screen') {
    if (!auth.isAuthenticated) {
      return <Navigate to="/account/login" replace />;
    }

    if (!isBackOfficeMaintenance) {
      return <Navigate to="/dashboard/management" replace />;
    }

    return <>{children}</>;
  }

  if (!auth.isAuthenticated) {
    return <>{children}</>;
  }

  if (isBackOfficeMaintenance && !isOnMaintenancePage) {
    return <Navigate to={BACKOFFICE_MAINTENANCE_ROUTE} replace />;
  }

  return <>{children}</>;
};

BackOfficeMaintenanceGuard.propTypes = {
  children: PropTypes.node as PropTypes.Validator<ReactNode>,
  mode: PropTypes.oneOf(['gate', 'screen']),
};

export default BackOfficeMaintenanceGuard;
